import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { RegistrationResultModel } from 'src/app/models/registration/registration-result.model';
import { RegistrationModel } from 'src/app/models/registration/registration.model';
import { LoadingService } from 'src/app/services/loading.service';
import { RegistrationService } from 'src/app/services/registration.service';
import { DxFormComponent } from 'devextreme-angular';
import { ToastrService } from 'ngx-toastr';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { forkJoin } from 'rxjs';
import { RegistrationLinkModel } from 'src/app/models/registration-link/registration-link.model';
import { RegistrationFieldConfigModel } from 'src/app/models/registration-field-configs/registration-field-config.model';
import { REGISTRATION_FIELD_LABEL_KEYS, REGISTRATION_FIELD_REQUIRED_MESSAGE_KEYS } from 'src/app/config/registration-field-config/registration-field.text-maps';
import { REGISTRATION_FIELD_KEYS } from 'src/app/config/registration-field-config/registration-field.keys';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
  standalone: false
})
export class RegisterComponent {
  @ViewChild('form', { static: false }) form!: DxFormComponent;
  
  registrationKey!: string;
  organisationUnitFooterHtml: SafeHtml | null = null;

  registrationLink!: RegistrationLinkModel;
  registrationResultModel?: RegistrationResultModel;

  organisationUnitLogoUrl: string | null = null;
  registrationFieldConfigs: RegistrationFieldConfigModel[] = [];
  
  readonly emailKey = REGISTRATION_FIELD_KEYS.email;
  
  isRegistered = false;
  formData: RegistrationModel = {
    email: '',
    firstName: '',
    lastName: '',
    companyName: '',
    agentNumber: null,
    iddNumber: null,
    phoneMobile: null,
    accepted: false
  };

  constructor(
    private route: ActivatedRoute,
    private loadingService: LoadingService,
    public translateService: TranslateService,
    private registrationService: RegistrationService,
    private toastrService: ToastrService,
    private sanitizer: DomSanitizer,
  ) {
    
  }

  ngOnInit(): void {
    this.registrationKey = this.route.snapshot.params['key'];
    this.loadTranslations();
    this.loadRegistrationData();
  }
  
  privacyPolicyTextHtml!: SafeHtml;
  serviceProvidedTextHtml!: SafeHtml;

  loadTranslations(): void {
    this.translateService.get('REGISTRATION.PRIVACY-POLICY').subscribe(translatedHere => {
      const privacyPolicyLink = 'https://skeddle.com/privacy-policy/';
      const privacyPolicyLinkElement = `<a href="${privacyPolicyLink}" class="text-link">${translatedHere}</a>`;

      this.translateService
        .get('REGISTRATION.PRIVACY-POLICY-TEXT', {
          privacy_policy: privacyPolicyLinkElement,
        })
        .subscribe(finalText => {
          this.privacyPolicyTextHtml = this.sanitizer.bypassSecurityTrustHtml(finalText);
        });
    });

    const serviceName = 'skeddle';
    const serviceProvidedLink = 'https://skeddle.com/';
    const serviceProvidedLinkElement = `<a href="${serviceProvidedLink}" class="text-link" target="_blank" rel="noopener noreferrer">${serviceName}</a>`;
    this.translateService
      .get('REGISTRATION.SERVICE-PROVIDED', {
        skeddle: serviceProvidedLinkElement,
      })
      .subscribe(finalText => {
        this.serviceProvidedTextHtml = this.sanitizer.bypassSecurityTrustHtml(finalText);
      });
  }

  loadRegistrationData(): void {
    this.loadingService
      .load(
        forkJoin({
          registrationLink: this.registrationService.getRegistrationLink(this.registrationKey),
          organisationUnitFooter: this.registrationService.getOrganisationUnitFooter(this.registrationKey),
          organisationUnitLogoUrl: this.registrationService.getOrganisationUnitLogoUrl(this.registrationKey),
          registrationFieldConfigs: this.registrationService.getRegistrationLinkFieldConfigs(this.registrationKey)
        })
      )
      .subscribe(({
        registrationLink, organisationUnitFooter, organisationUnitLogoUrl,
        registrationFieldConfigs
      }) => {
        this.registrationLink = registrationLink;
        this.organisationUnitFooterHtml = this.sanitizer.bypassSecurityTrustHtml(organisationUnitFooter || '');
        this.organisationUnitLogoUrl = organisationUnitLogoUrl;
        this.registrationFieldConfigs = registrationFieldConfigs
      })
  }

  register(): void {
    if (!this.form.instance.validate().isValid) {
       this.toastrService.error(
        this.translateService.instant('VALIDATIONS.FORM-INVALID')
      );
      return;
    }

    if (!this.formData.accepted) {
       this.toastrService.error(
        this.translateService.instant('VALIDATIONS.ACCEPTED-REQUIRED')
      );
      return;
    }

    this.loadingService
      .load(
        this.registrationService.register(
          this.registrationKey,
          this.formData
        )
      )
      .subscribe({
        next: (registrationResultModel) => {
          this.registrationResultModel = registrationResultModel;
          this.isRegistered = true;
        },
        error: (error) => {
          console.error('Error registering new person:', error.error);
        }
      });
  }

  mustBeTrue = () => {
    return true;
  };

  customNameValidation = (e: any) => {
    return /^[\p{L}-]+$/u.test(e.value);
  };

  getLabelKey(key: string): string {
    return REGISTRATION_FIELD_LABEL_KEYS[key] ?? key;
  }

  getRequiredMessageKey(key: string): string {
    return REGISTRATION_FIELD_REQUIRED_MESSAGE_KEYS[key] ?? key;
  }
}
